import { IS_NATIVE, PLATFORM, SHOW_DEV_TOOLS, IS_APPLE } from "../config.js";
import { writeCacheFile } from "./files.js";
import { loadCapacitorCore } from "./plugins.js";

/**
 * AR entry point.
 *
 * iOS uses USDZ + AR Quick Look. Android hands a locally generated .glb to ARCore Scene
 * Viewer through a content:// URI — undocumented, so every call reports whether it
 * actually opened rather than assuming.
 *
 * Both native platforms need a plugin. The `<a rel="ar">` anchor the web build uses for
 * Quick Look is a Safari feature, not a WebKit one: WKWebView never intercepts the click,
 * so in the app it is a silent no-op. Native iOS goes to QLPreviewController instead.
 */

/**
 * Whether to show the AR button at all. Native is always a yes — Android is probed
 * properly at tap time — so the two platform branches are just IS_NATIVE.
 *
 * Uses the same `IS_APPLE` check `exportToUSDZ` takes its Quick Look branch on. A bare
 * /iPad|iPhone|iPod/ test misses iPadOS 13+, which reports a Macintosh UA: the button
 * would hide on a desktop-mode iPad that would in fact have opened Quick Look.
 */
export function arAvailable() {
  return IS_NATIVE || IS_APPLE || SHOW_DEV_TOOLS;
}

/**
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function presentAR(app) {
  if (IS_NATIVE && PLATFORM === "android") return presentSceneViewer(app);
  if (IS_NATIVE && PLATFORM === "ios") return presentQuickLook(app);
  const manager = await app.ensureUSDZExportManager();
  await manager.exportToUSDZ();
  return { ok: true };
}

async function presentQuickLook(app) {
  const manager = await app.ensureUSDZExportManager();
  const blob = await manager.buildUSDZBlob();
  if (!blob) return { ok: false, reason: "empty" };
  const megabytes = (blob.size / 1e6).toFixed(1);

  const uri = await writeCacheFile(blob, "ar/layout.usdz");

  const { Capacitor } = await loadCapacitorCore();
  // Quick Look takes a filesystem path, not the capacitor:// URL the WebView uses.
  const path = decodeURIComponent(uri.replace(/^file:\/\//, ""));

  try {
    const { opened, reason } = await Capacitor.Plugins.ARQuickLook.openModel({ path });
    return opened ? { ok: true, megabytes } : { ok: false, reason, megabytes };
  } catch (error) {
    console.warn("[ar] Quick Look failed:", error);
    return { ok: false, reason: "quick-look-error" };
  }
}

async function presentSceneViewer(app) {
  const { GLBExportManager } = await import("../export/GLBExportManager.js");
  const blob = await new GLBExportManager(app).exportToGLB();
  if (!blob) return { ok: false, reason: "empty" };
  const megabytes = (blob.size / 1e6).toFixed(1);

  const uri = await writeCacheFile(blob, "ar/layout.glb");

  const { Capacitor } = await loadCapacitorCore();
  // Scene Viewer needs a filesystem path, not the capacitor:// URL the WebView uses.
  const path = uri.replace(/^file:\/\//, "");

  try {
    const { opened, reason } = await Capacitor.Plugins.SceneViewer.openModel({
      path,
      title: "FEYDOM",
    });
    // Scene Viewer fails silently out-of-process, so carry the size back: if the
    // model loads nowhere, that number is the first thing to check.
    return opened ? { ok: true, megabytes } : { ok: false, reason, megabytes };
  } catch (error) {
    console.warn("[ar] Scene Viewer failed:", error);
    return { ok: false, reason: "scene-viewer-error" };
  }
}
